import { useSearchParams } from 'react-router-dom'
import { Button, Card, EmptyState, InlineError, Skeleton } from '../../components/ui'
import { useTenant } from '../../tenant/useTenant'
import { useGetMyBookingsQuery, type BookingFilter } from '../../services/accountApi'
import { BookingCard } from './BookingCard'
import { cancelPolicyNote } from './bookingDisplay'
import { cn } from '../../utils/cn'

const FILTERS: { key: BookingFilter; label: string }[] = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
]

/** I6 — the account landing: upcoming and past appointments. */
export function BookingsPage() {
  const { slug, bootstrap } = useTenant()
  const [params, setParams] = useSearchParams()

  const filter: BookingFilter = params.get('filter') === 'past' ? 'past' : 'upcoming'
  const page = Number(params.get('page')) > 0 ? Number(params.get('page')) : 1

  const { data, isFetching, isError, error, refetch } = useGetMyBookingsQuery({ slug, status: filter, page })

  const cancelAllowed = bootstrap.flags.customer_cancel_allowed
  const policy = cancelPolicyNote(bootstrap.flags.cancel_cutoff_hours)

  const update = (patch: Record<string, string | null>) => {
    setParams((current) => {
      const next = new URLSearchParams(current)
      for (const [key, value] of Object.entries(patch)) {
        if (value === null) next.delete(key)
        else next.set(key, value)
      }
      return next
    })
  }

  const past = filter === 'past'

  return (
    <div className="flex flex-col gap-6">
      <div role="tablist" aria-label="Filter appointments" className="flex gap-2 overflow-x-auto">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={filter === item.key}
            onClick={() => update({ filter: item.key, page: null })}
            className={cn(
              'whitespace-nowrap rounded-full px-6 py-3 font-label-caps text-label-caps uppercase transition-colors duration-300',
              filter === item.key
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-primary/5 hover:text-primary',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Stitch heads each half of the account list with a display section title
          and its glyph; the past half is stated more quietly than the upcoming one. */}
      <h2
        className={cn(
          'flex items-center gap-3 font-display text-headline-md',
          past ? 'text-on-surface-variant opacity-70' : 'text-primary',
        )}
      >
        <span aria-hidden="true" className="material-symbols-outlined">
          {past ? 'history' : 'event'}
        </span>
        {past ? 'Past visits' : 'Upcoming'}
      </h2>

      {filter === 'upcoming' && cancelAllowed && policy && (
        <p className="flex items-start gap-2 font-body-md text-sm text-on-surface-variant">
          <span className="material-symbols-outlined text-[18px]">info</span>
          {policy}
        </p>
      )}

      {isError && <InlineError error={error} onRetry={() => void refetch()} />}

      {isFetching && !data && (
        <div className="flex flex-col gap-4">
          {[0, 1, 2].map((i) => (
            <Card key={i} variant="filled">
              <Skeleton lines={3} />
            </Card>
          ))}
        </div>
      )}

      {data && data.items.length === 0 && (
        <EmptyState
          icon={filter === 'upcoming' ? 'event_available' : 'history'}
          title={filter === 'upcoming' ? 'No upcoming appointments' : 'No past appointments yet'}
          body={
            filter === 'upcoming'
              ? 'Book a visit and it will show up here.'
              : 'Your visit history will appear here after your first appointment.'
          }
          action={
            filter === 'upcoming' ? (
              <Button to={`/${slug}/book`} size="lg">
                Book an appointment
              </Button>
            ) : undefined
          }
        />
      )}

      {data && data.items.length > 0 && (
        <div className={cn('flex flex-col', past ? 'gap-4 opacity-70' : 'gap-6', isFetching && 'opacity-60')}>
          {data.items.map((booking) => (
            <BookingCard key={booking.id} booking={booking} cancelAllowed={cancelAllowed} past={past} />
          ))}
        </div>
      )}

      {data && data.lastPage > 1 && (
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outlined"
            size="sm"
            disabled={page <= 1}
            onClick={() => update({ page: String(page - 1) })}
          >
            Previous
          </Button>
          <span className="font-body-md text-sm text-on-surface-variant">
            Page {data.page} of {data.lastPage}
          </span>
          <Button
            variant="outlined"
            size="sm"
            disabled={page >= data.lastPage}
            onClick={() => update({ page: String(page + 1) })}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
